import { indexBy, isObject, uniq } from 'remeda';
import {
  AccountModel,
  AccountRawData,
  GeneralizedAccountInfo,
  getAccountHashFromPublicKey,
  getAccountInfoLogo,
  getAssociatedKeysFromAccountRawData,
  getCentralizedAccountInfoLogo,
} from '@block-explorer/entities';
import { isNonNullable } from './guards';
import { deriveSplitDataFromNamedKeyValue } from '../domain';
import { isObjEmpty } from './object.tsx';
import { PublicKey as CLPublicKey } from 'casper-js-sdk';
import { NamedKeyPrefix } from '@make-software/cspr-design';

export const includesAccountInVerifiedList = (
  accountInfoVerifiedHashes: string[] | undefined,
  unsafePublicKey
): boolean => {
  const verifiedAccountHashes = indexBy(accountInfoVerifiedHashes || [], (i) =>
    i.toLowerCase()
  );

  if (!isValidPublicKey(unsafePublicKey)) {
    return false;
  }

  const hash = AccountModel({
    publicKeyHex: unsafePublicKey,
  }).getAccountHash();

  return !!verifiedAccountHashes[hash.toLowerCase()];
};

export const isValidAccountHash = (
  accountHash?: string | null
): accountHash is string => {
  if (accountHash == null) {
    return false;
  }

  const validHashRegExp = new RegExp('^([0-9A-Fa-f]){64}$');
  return validHashRegExp.test(accountHash.trim());
};

export const isValidPublicKey = (
  publicKey?: string | null
): publicKey is string => {
  if (publicKey == null) {
    return false;
  }

  const ED25519_KEY_ALGO_PREFIX = '01';
  const SECP256K1_KEY_ALGO_PREFIX = '02';
  const publicKeyRegExp = new RegExp(/^[a-fA-F0-9]*$/);

  if (!publicKeyRegExp.test(publicKey)) {
    return false;
  }

  const prefix = publicKey.slice(0, 2);
  if (
    (prefix === ED25519_KEY_ALGO_PREFIX && publicKey.length !== 66) ||
    (prefix === SECP256K1_KEY_ALGO_PREFIX && publicKey.length !== 68)
  ) {
    return false;
  }

  try {
    CLPublicKey.fromHex(publicKey).toHex();
    return true;
  } catch (error) {
    return false;
  }
};

export const isValidCsprName = (name: string) =>
  name && name.length > 5 && name.length < 256 && name.endsWith('.cspr');

export const isValidURef = (uref: string) => {
  const validURefRegExp = new RegExp('^uref-[0-9a-f]{64}-\\d{3}$', 'i');

  return uref && validURefRegExp.test(uref.trim());
};

export const findHash = (value: string | undefined | null): string | null => {
  const hash = value && value.match(new RegExp('([0-9A-Fa-f]){64}'));

  return hash ? hash[0] : null;
};

export const hasAccountHashPrefix = (accountHash?: string | null) =>
  accountHash?.startsWith(NamedKeyPrefix.ACCOUNT_HASH);

/**
 * Method, specific for case, where `publicKey == '00'` means that it is a Immediate Switch Block
 * NOTE: as part of Casper network node v1.5, the node software now creates an "immediate switch block" on upgrades;
 * there are no rewards for this block. it simply captures the information after application of the upgrade,
 * which allows this to be deterministically detected
 * */
export const getProposerAccountHash = (proposerPublicKey: string) =>
  isValidPublicKey(proposerPublicKey)
    ? AccountModel({
        publicKeyHex: proposerPublicKey,
      }).getAccountHash()
    : proposerPublicKey;

/**
 * Considers only `parsed` field of the object `args`
 * @param data
 * @param keys
 */
export const deriveHashListByKeysFromDeployArgsObject = (
  data: any,
  keys: string[]
) => {
  const result: string[] = [];

  if (!data) {
    return null;
  }

  const uniqKeys = uniq(keys);

  data.forEach((obj) => {
    uniqKeys.forEach((key) => {
      if (
        key in obj.args &&
        obj.args[key] !== null &&
        obj.args[key] !== undefined
      ) {
        if (Array.isArray(obj.args[key].parsed)) {
          return;
        }

        const hash = isObject(obj.args[key].parsed)
          ? (Object.values(obj.args[key].parsed)[0] as string)
          : (obj.args[key].parsed as string);

        if (hash) {
          const { hash: hashWithoutPrefix } =
            deriveSplitDataFromNamedKeyValue(hash);
          result.push(isValidURef(hash) ? hash : hashWithoutPrefix);
        }
      }
    });
  });

  return result.length > 0 ? result.filter(isNonNullable) : null;
};

export const deriveHashListByKeysFromObject = (data: any, keys: string[]) => {
  const result: string[] = [];

  if (!data) {
    return null;
  }

  data.forEach((obj) => {
    keys.forEach((key) => {
      if (key in obj && obj[key] !== null && obj[key] !== undefined) {
        const hash = obj[key] as string;

        if (hash) {
          const { hash: hashWithoutPrefix } =
            deriveSplitDataFromNamedKeyValue(hash);
          result.push(hashWithoutPrefix);
        }
      }
    });
  });

  return result.length > 0 ? result.filter(isNonNullable) : null;
};

export const getAccountWeightFromAccountRawData = (
  publicKey: string,
  accountRawData: AccountRawData | null
): number => {
  const associatedKeys = getAssociatedKeysFromAccountRawData(
    accountRawData
  ) as any[];
  const hash = `account-hash-${getAccountHashFromPublicKey(publicKey)}`;

  const currentAccount = associatedKeys?.find((account) => {
    return account.account_hash === hash;
  });

  return currentAccount?.weight;
};

/**
 * Derive account info from accountInfo or centralizedAccountInfo.
 * Reason for doing that, we have different interfaces for those objects.
 * @param accountInfoObj : AccountInfoResult | AccountCentralizedInfo
 */
export const deriveAccountInfo = (
  accountInfoObj: any
): GeneralizedAccountInfo | null => {
  if (accountInfoObj == null) {
    return null;
  }

  const hasInfoStandard = !isObjEmpty(accountInfoObj?.info);

  if (hasInfoStandard) {
    return {
      name: accountInfoObj?.info?.owner?.name,
      logo: getAccountInfoLogo(accountInfoObj),
    };
  }

  return {
    name: accountInfoObj?.name,
    logo: getCentralizedAccountInfoLogo(accountInfoObj),
  };
};
